// U/P odds — the Open Positions "U/P" column/stat: Unabated consensus fair
// probability next to the Polymarket book mid for the same market, both
// side-native so they read against the row's Avg / Now (a NO holder sees
// 100 − the YES-team value). Replaced the positions 1m Δ price chip — the
// Open Orders surfaces keep theirs.
//
// Values are computed by PositionTracker's coarse per-second sweep (same
// clock as the divergence badge): consensus via marketStore.getConsensusFor
// (tenth-of-a-cent resolution when hydra sends tenths), poly mid via bookMid
// on the paired game slot's book. A poly book with no recent orderbook frame
// renders muted — a frozen feed must not read as a live price ("Stale prices
// mask dead hydra feeds", CLAUDE.md).

import {
  DIVERGENCE_THRESHOLD_CENTS,
  POLY_STALE_MS,
} from '@/lib/divergence/polyKalshiDivergence';

export interface UPOdds {
  /** Side-native Unabated consensus fair prob, cents (tenth precision when
   *  hydra delivers tenths; integer cents on pre-rollout hydra). Null when
   *  no consensus has arrived for the game. */
  uCents: number | null;
  /** Side-native Polymarket book mid, cents. Null when the game has no
   *  paired poly slot or the book is one-sided. */
  pCents: number | null;
  /** Poly orderbook recent enough to trust (same POLY_STALE_MS gate as the
   *  divergence badge). False renders the P value muted. */
  polyFresh: boolean;
}

/** YES-team probability → the holder's side. NO backs the complement. */
export function sideNativeProbCents(
  yesCents: number | null,
  side: 'yes' | 'no',
): number | null {
  if (yesCents == null) return null;
  return side === 'yes' ? yesCents : 100 - yesCents;
}

function fmtProb(v: number | null): string {
  if (v == null) return '—';
  const r = Math.round(v * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
}

/** Unabated-vs-Kalshi divergence pill — the consensus counterpart of the
 *  violet Poly DivergenceBadge, sharing the card's top-right badge cluster
 *  and the SAME trigger threshold (>= DIVERGENCE_THRESHOLD_CENTS). Both
 *  inputs are side-native, so the sign reads for the holder: +N¢ = the
 *  sharp consensus prices your side ABOVE the current Kalshi mark. Poly's
 *  badge gates on book freshness; consensus has no book age — a game with
 *  no (or pruned) consensus simply has uCents null and renders nothing.
 *  Cyan, not violet, so a double pop (U + Poly) stays scannable. */
export function UnabatedDivergenceBadge({
  odds,
  nowCents,
  className = '',
}: {
  odds: UPOdds | null | undefined;
  /** Side-native current Kalshi mark (the card's "now" price). */
  nowCents: number | null;
  className?: string;
}) {
  const u = odds?.uCents ?? null;
  if (u == null || nowCents == null) return null;
  const signed = u - nowCents;
  const mag = Math.abs(signed);
  if (mag < DIVERGENCE_THRESHOLD_CENTS) return null;
  const dir = signed > 0 ? 'above' : 'below';
  return (
    <span
      title={
        `Unabated consensus prices your side ${fmtProb(mag)}¢ ${dir} the current ` +
        `Kalshi mark (U ${fmtProb(u)}¢ / now ${fmtProb(nowCents)}¢). Consensus signal only.`
      }
      className={`inline-flex shrink-0 items-center gap-1 rounded-full bg-cyan-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-cyan-300 light:bg-cyan-500/20 light:text-cyan-700 ${className}`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-cyan-400 light:bg-cyan-600" />
      U {signed > 0 ? '+' : '−'}{fmtProb(mag)}¢
    </span>
  );
}

/** The "62.4/61.5" pair — shared by the table cell and the card StatLine so
 *  the two views can't drift. Unresolved halves render as a dash ("—/—" when
 *  neither source resolved: non-moneyline markets, unpaired games). */
export function UPOddsValue({ odds }: { odds: UPOdds | null | undefined }) {
  const u = odds?.uCents ?? null;
  // P is a BOOK MID — (bid+ask)/2 — so a 1¢-wide book yields an x.5 that
  // Polymarket never actually quotes. Display it as an integer (round the
  // already-side-native value; rounding before the NO flip would disagree
  // with the YES read by 1¢ on exact halves). U keeps its tenths — those
  // are real consensus precision, not arithmetic residue (Dan, Jul 8).
  const p = odds?.pCents != null ? Math.round(odds.pCents) : null;
  const polyStale = p != null && !odds?.polyFresh;
  return (
    <span className="whitespace-nowrap">
      <span
        className={u != null ? 'text-foreground' : 'text-muted-foreground'}
        title="Unabated consensus fair probability (side-native)"
      >
        {fmtProb(u)}
      </span>
      <span className="text-muted-foreground">/</span>
      <span
        className={p != null && !polyStale ? 'text-foreground' : 'text-muted-foreground'}
        title={
          polyStale
            ? `Polymarket mid (side-native) — book stale, no orderbook frame in ${Math.round(POLY_STALE_MS / 60_000)}+ min`
            : 'Polymarket book mid (side-native)'
        }
      >
        {fmtProb(p)}
      </span>
    </span>
  );
}
